import React, { useState, useEffect, useContext } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import FormControl from "react-bootstrap/FormControl";
import Spinner from "react-bootstrap/Spinner";
import ProgressBar from "react-bootstrap/ProgressBar";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { toast, ToastContainer } from "react-toastify";
import {
  FiExternalLink,
  FiRepeat,
  FiFileText,
  FiBookmark,
  FiTrash2,
  FiPlus,
  FiShuffle,
} from "react-icons/fi";
import "react-toastify/dist/ReactToastify.min.css";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "./Topic.css";
import { ThemeContext } from "../../App";
import { slugify } from "../../utils/slug";
import { topicEmoji } from "../../utils/topicMeta";
import { parseProblemUrl } from "../../utils/parseProblemUrl";

const DIFFICULTY_CYCLE = ["", "Easy", "Medium", "Hard"];
const DIFFICULTY_PILL = { Easy: "green", Medium: "yellow", Hard: "red", "": "gray" };
const DIFFICULTY_LABEL = { Easy: "Easy", Medium: "Medium", Hard: "Hard", "": "—" };

export default function Topic({ data, updateData, addProblem, deleteProblem }) {
  const dark = useContext(ThemeContext);

  const [select, setSelected] = useState([]);
  const [isBookmarkSortFilterSelected, setIsBookmarkSortFilterSelected] = useState(false);
  const [isSelectedComplete, setSelectedComplete] = useState(true);
  const [sortMode, setSortMode] = useState({ dataField: "_is_selected", order: "asc" });
  const [questionsTableData, setQuestionsTableData] = useState([]);
  const [topicName, setTopicName] = useState("");

  // modals
  const [noteModal, setNoteModal] = useState({ open: false, index: -1 });
  const [noteDraft, setNoteDraft] = useState("");
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ Problem: "", URL: "", Difficulty: "" });
  const [addError, setAddError] = useState("");

  const topicSlug = data ? slugify(data.topicName) : "";

  // central commit helper — recomputes progress and pushes to App + DB
  function commit(questions) {
    const done = questions.filter((q) => q.Done).length;
    updateData(slugify(topicName), { started: done > 0, doneQuestions: done, questions }, data.position);
  }

  function mutate(index, patch) {
    const questions = data.questions.map((q, i) => (i === index ? { ...q, ...patch } : q));
    commit(questions);
    return questions;
  }

  // func() triggered when a question is marked done
  function handleSelect(row, isSelect) {
    const id = row.id;
    const q = data.questions[id];
    mutate(id, {
      Done: isSelect,
      SolvedAt: isSelect ? q.SolvedAt || new Date().toISOString() : q.SolvedAt,
    });
    if (isSelectedComplete) displayToast(isSelect, id);
  }

  function handleBookmark(index) {
    mutate(index, { Bookmark: !data.questions[index].Bookmark });
  }

  function handleRevise(index) {
    mutate(index, { Revise: !data.questions[index].Revise });
  }

  function cycleDifficulty(index) {
    const cur = data.questions[index].Difficulty || "";
    const next = DIFFICULTY_CYCLE[(DIFFICULTY_CYCLE.indexOf(cur) + 1) % DIFFICULTY_CYCLE.length];
    mutate(index, { Difficulty: next });
  }

  function openNote(index) {
    setNoteDraft(data.questions[index].Notes || "");
    setNoteModal({ open: true, index });
  }

  function saveNote() {
    if (noteModal.index < 0) return;
    mutate(noteModal.index, { Notes: noteDraft.trim() });
    setNoteModal({ open: false, index: -1 });
  }

  function handleDelete(index) {
    if (window.confirm(`Delete "${data.questions[index].Problem}" from this topic?`)) {
      deleteProblem(topicSlug, index);
    }
  }

  function submitAddProblem() {
    if (!addForm.Problem.trim()) {
      setAddError("Problem name is required.");
      return;
    }
    addProblem(topicSlug, { ...addForm, Problem: addForm.Problem.trim() }, () => {
      setAddForm({ Problem: "", URL: "", Difficulty: "" });
      setAddError("");
      setAddModal(false);
    });
  }

  // Paste-a-link helper: auto-fill the title from the URL when the name is
  // still empty (so you can add a problem with just a link).
  function handleAddUrlChange(value) {
    setAddForm((prev) => {
      const next = { ...prev, URL: value };
      if (!prev.Problem.trim()) {
        const parsed = parseProblemUrl(value);
        if (parsed.problem) next.Problem = parsed.problem;
      }
      return next;
    });
  }

  // build table rows from `data`
  useEffect(() => {
    if (data === undefined) return;
    let doneQuestion = [];
    let tableData = data.questions.map((question, index) => {
      if (question.Done) doneQuestion.push(index);
      const diff = question.Difficulty || "";
      return {
        id: index,
        question: (
          <div className="q-cell">
            <a
              href={question.URL || undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="question-link"
            >
              {question.Problem}
            </a>
            {question.Custom ? <span className="nb-pill blue q-custom">custom</span> : null}
          </div>
        ),
        links: (
          <div className="link-cell">
            {question.URL ? (
              <button
                type="button"
                className="nb-iconbtn"
                title="Open problem"
                aria-label="Open problem"
                onClick={() => window.open(question.URL, "_blank")}
              >
                <FiExternalLink size={15} />
              </button>
            ) : null}
          </div>
        ),
        controls: (
          <div className="control-row">
            <button
              type="button"
              className={`nb-pill ${DIFFICULTY_PILL[diff]} diff-pill`}
              title="Click to set difficulty"
              onClick={() => cycleDifficulty(index)}
            >
              {DIFFICULTY_LABEL[diff]}
            </button>

            <button
              type="button"
              className={`nb-iconbtn ${question.Revise ? "active" : ""}`}
              title={question.Revise ? "Unmark revise" : "Mark for revision"}
              aria-label="Mark for revision"
              onClick={() => handleRevise(index)}
            >
              <FiRepeat size={15} />
            </button>

            <button
              type="button"
              className={`nb-iconbtn ${question.Notes && question.Notes.length ? "active" : ""}`}
              title={question.Notes && question.Notes.length ? "View / edit notes" : "Add notes"}
              aria-label="Notes"
              onClick={() => openNote(index)}
            >
              <FiFileText size={15} />
            </button>

            <button
              type="button"
              className={`nb-iconbtn ${question.Bookmark ? "active" : ""}`}
              title={question.Bookmark ? "Remove bookmark" : "Add bookmark"}
              aria-label="Bookmark"
              onClick={() => handleBookmark(index)}
            >
              <FiBookmark size={15} />
            </button>

            {question.Custom ? (
              <button
                type="button"
                className="nb-iconbtn"
                title="Delete this custom problem"
                aria-label="Delete problem"
                onClick={() => handleDelete(index)}
              >
                <FiTrash2 size={15} />
              </button>
            ) : null}
          </div>
        ),
        _is_selected: question.Done,
        Bookmark: question.Bookmark,
        _search_text: question.Problem,
      };
    });
    setQuestionsTableData(tableData);
    setTopicName(data.topicName);
    setSelected(doneQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // trigger an information message for user on select change
  function displayToast(isSelect, id) {
    const { icon, dir, type } = {
      type: isSelect ? "Done" : "Incomplete",
      icon: isSelect ? "🎉" : "🙇🏻‍♂️",
      dir: isSelect ? "👇🏻" : "👆🏻",
    };
    const doneCount = data.questions.filter((q) => q.Done).length + (isSelect ? 1 : -1);
    const title = `${doneCount}/${data.questions.length} Done`;
    const Card = (
      <>
        <p>
          {title} <span className="emojiFix">{icon}</span>
        </p>
        <p className="toast-subtitle">Question pushed {dir} the table.</p>
      </>
    );
    toast(Card, { className: `toast-${type}`, autoClose: 2000, closeButton: true });
  }

  const Sorter = (x) => {
    if (!x) {
      setSortMode({ dataField: "Bookmark", order: "desc" });
      setSelectedComplete(x);
    } else {
      setSortMode({ dataField: "_is_selected", order: "asc" });
      setSelectedComplete(x);
    }
    setIsBookmarkSortFilterSelected(!x);
  };

  const SearchBar = (props) => {
    const handleChange = (e) => props.onSearch(e.target.value);
    return (
      <div className="topic-toolbar">
        <RandomButton data={data} />
        <FormControl
          className="topic-search"
          placeholder="Search problems…"
          aria-label="Search Question"
          onChange={handleChange}
        />
        <span className="nb-pill green count-pill">
          {data.doneQuestions}/{data.questions.length} done
        </span>
        <button
          type="button"
          className={`nb-iconbtn ${isBookmarkSortFilterSelected ? "active" : ""}`}
          title={isBookmarkSortFilterSelected ? "Reset bookmark sort" : "Show bookmarks first"}
          aria-label="Sort by bookmark"
          onClick={() => Sorter(isBookmarkSortFilterSelected)}
        >
          <FiBookmark size={16} />
        </button>
        <button
          type="button"
          className="nb-iconbtn"
          title="Add a custom problem"
          aria-label="Add a custom problem"
          onClick={() => setAddModal(true)}
        >
          <FiPlus size={16} />
        </button>
      </div>
    );
  };

  const columns = [
    {
      dataField: "id",
      text: "#",
      headerStyle: { width: "44px", textAlign: "center" },
      style: { cursor: "pointer", textAlign: "center" },
      events: {
        onClick: (e, column, columnIndex, row) => handleSelect(row, !row._is_selected),
      },
    },
    { dataField: "question", text: "Problem", headerStyle: { textAlign: "left", width: "52%" } },
    { dataField: "links", text: "Links", headerStyle: { textAlign: "center", width: "110px" } },
    { dataField: "controls", text: "", headerStyle: { textAlign: "right", width: "230px" } },
    { dataField: "_is_selected", text: "Is Selected", hidden: true, sort: true },
    { dataField: "_search_text", text: "Search Text", hidden: true },
    { dataField: "Bookmark", text: "Bookmark", hidden: true },
  ];
  const selectRow = {
    mode: "checkbox",
    classes: "nb-row-done",
    selected: select,
    onSelect: handleSelect,
    hideSelectAll: true,
  };

  if (data === undefined) {
    return (
      <div className="nb-loading">
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  const pct = data.questions.length ? Math.round((data.doneQuestions / data.questions.length) * 100) : 0;

  return (
    <>
      <div className="page-header">
        <div className="page-emoji" role="img" aria-label="topic">
          {topicEmoji(topicName)}
        </div>
        <h1 className="page-title">{topicName}</h1>
        <p className="page-sub">
          {data.doneQuestions} of {data.questions.length} solved · {pct}% complete
        </p>
        {data.doneQuestions ? (
          <div className="page-progress">
            <ProgressBar now={pct} />
          </div>
        ) : null}
      </div>

      <ToolkitProvider keyField="id" data={questionsTableData} columns={columns} search>
        {(props) => (
          <div>
            <div className="header-rand">{SearchBar({ ...props.searchProps })}</div>
            <div className="nb-table-wrap" style={{ overflowAnchor: "none" }}>
              <BootstrapTable {...props.baseProps} selectRow={selectRow} sort={sortMode} classes={dark ? "dark-table" : ""} />
            </div>
          </div>
        )}
      </ToolkitProvider>
      <ToastContainer />

      {/* NOTES MODAL */}
      <Modal show={noteModal.open} onHide={() => setNoteModal({ open: false, index: -1 })} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            📝 {noteModal.index >= 0 ? data.questions[noteModal.index].Problem : "Notes"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={8}
            maxLength={4000}
            value={noteDraft}
            placeholder="Approach, edge cases, complexity, links, gotchas..."
            onChange={(e) => setNoteDraft(e.target.value)}
          />
          <small className="text-muted">{noteDraft.length}/4000</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setNoteModal({ open: false, index: -1 })}>
            Close
          </Button>
          <Button variant="success" onClick={saveNote}>
            Save notes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ADD PROBLEM MODAL */}
      <Modal show={addModal} onHide={() => setAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.1rem" }}>➕ Add a problem to {topicName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              submitAddProblem();
            }}
          >
            <Form.Group>
              <Form.Label>Problem name *</Form.Label>
              <Form.Control
                autoFocus
                value={addForm.Problem}
                onChange={(e) => setAddForm({ ...addForm, Problem: e.target.value })}
                placeholder="e.g. Trapping Rain Water"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Primary URL</Form.Label>
              <Form.Control
                value={addForm.URL}
                onChange={(e) => handleAddUrlChange(e.target.value)}
                placeholder="https://leetcode.com/problems/..."
              />
              <Form.Text muted>Paste a link and the name fills in automatically.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Difficulty</Form.Label>
              <Form.Control as="select" value={addForm.Difficulty} onChange={(e) => setAddForm({ ...addForm, Difficulty: e.target.value })}>
                <option value="">— none —</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </Form.Control>
            </Form.Group>
            {addError ? <p style={{ color: "#dc3545" }}>{addError}</p> : null}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAddModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={submitAddProblem}>
            Add problem
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function RandomButton({ data }) {
  let min = 0;
  let max = Math.max(1, data.questions.length - 1);
  const [rnd, setRnd] = useState(Math.floor(Math.random() * (max - min)) + min);
  function pickRandomHandler() {
    setRnd(Math.floor(Math.random() * (max - min)) + min);
  }
  return (
    <a
      className="nb-btn random-btn"
      onClick={pickRandomHandler}
      onContextMenu={pickRandomHandler}
      href={data.questions.length ? data.questions[rnd].URL : undefined}
      target="_blank"
      rel="noopener noreferrer"
    >
      <FiShuffle size={15} /> Pick random
    </a>
  );
}
